import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { articles as rawArticles } from "../data/articles";

type Service = {
  title: string;
  duration: string;
  price: string;
  description: string;
  bg?: string;
};

const services: Service[] = [
  {
    title: "Мануальная терапия",
    duration: "30–45 минут",
    price: "10 000 ₽",
    description:
      "Мануальные техники строго по показаниям, в контексте неврологической картины и общего состояния. Часть комплексного подхода.",
  },
  {
    title: "Неврологическая консультация",
    duration: "до 50 минут",
    price: "10 000 ₽",
    description:
      "Длительность приёма варьируется по клинической задаче. При первой записи бронь удерживается 12 часов и подтверждается предоплатой.",
  },
  {
    title: "Индивидуальная психотерапевтическая сессия",
    duration: "50 минут",
    price: "от 15 000 ₽",
    description:
      "Цена формируется индивидуально, учитывая срочность и сложность запроса. Возможное продление: +15 минут (2 500 ₽) или +30 минут (5 000 ₽).",
  },
];

const sortByDateDesc = (items: typeof rawArticles) =>
  [...items].sort((a, b) => {
    const [da, ma, ya] = a.date.split(".").map(Number);
    const [db, mb, yb] = b.date.split(".").map(Number);
    return yb - ya || mb - ma || db - da;
  });

export const HomePage: React.FC = () => {
  const [selectedService, setSelectedService] = useState<Service>(services[0]);
  const [slide, setSlide] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const sortedArticles = useMemo(() => sortByDateDesc(rawArticles), []);
  const featuredArticles = sortedArticles.slice(0, 8);

  const handleSwipeStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleSwipeEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    const threshold = 40;
    if (delta > threshold) {
      setSlide((s) => (s + services.length - 1) % services.length);
    } else if (delta < -threshold) {
      setSlide((s) => (s + 1) % services.length);
    }
    setTouchStartX(null);
  };

  return (
    <main className="page">
      <section className="hero" id="hero">
        <div className="hero__overlay"></div>
        <div className="hero__content">
          <div className="hero__inner">
            <p className="eyebrow">НЕВРОЛОГИЯ • ПСИХОТЕРАПИЯ • МАНУАЛЬНАЯ ТЕРАПИЯ</p>
            <h1 className="hero__title">
              Невролог и психотерапевт
              <br />
              премиального уровня
              <br />
              в Москве
            </h1>
            <p className="hero__subtitle">
              Диагностика, терапия и сопровождение — очно и онлайн. Индивидуальный подход, доказательная медицина,
              комфорт и конфиденциальность.
            </p>
            <div className="hero__actions">
              <a className="btn btn--primary" href="#booking">
                Записаться
              </a>
            </div>
            <div className="hero__meta">
              <div className="meta__item">
                <span className="meta__label">ФОКУС</span>
                <span className="meta__value">Диагностика и терапия</span>
              </div>
              <div className="meta__divider"></div>
              <div className="meta__item">
                <span className="meta__label">ФОРМАТ</span>
                <span className="meta__value">Онлайн и очно</span>
              </div>
              <div className="meta__divider"></div>
              <div className="meta__item">
                <span className="meta__label">ГОРОД</span>
                <span className="meta__value">Москва</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="booking" id="booking">
        <div className="booking__inner">
          <div className="booking__map">
            <div className="map__header">
              <div className="map__icon">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3.5c-3.1 0-5.5 2.5-5.5 5.6 0 4.2 5.5 11.4 5.5 11.4s5.5-7.2 5.5-11.4C17.5 6 15.1 3.5 12 3.5Zm0 7.3a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6Z" />
                </svg>
              </div>
              <div>
                <p className="map__label">Адрес</p>
                <p className="map__title">город Москва, ЦАО, улица Солянка, дом 15</p>
              </div>
            </div>
            <div className="map__canvas">
              <iframe
                className="map__iframe"
                title="Карта — Солянка 15, Москва"
                src="https://www.openstreetmap.org/export/embed.html?bbox=37.6320%2C55.7480%2C37.6420%2C55.7540&layer=mapnik&marker=55.7518%2C37.6378"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          <div className="booking__form">
            <div className="booking__form-header">
              <h3>Запись на прием</h3>
              <p>Оставьте контакты и выберите услугу. Свяжемся для подтверждения времени.</p>
            </div>
            <form className="form" onSubmit={(e) => e.preventDefault()}>
              <div className="form__row">
                <label className="field">
                  <span className="field__label">Имя и фамилия</span>
                  <input type="text" name="name" placeholder="Например: Иван Петров" required />
                </label>
                <label className="field">
                  <span className="field__label">Телефон</span>
                  <div className="phone">
                    <span className="flag flag--ru" aria-hidden="true"></span>
                    <span className="phone__prefix">+7</span>
                    <input type="tel" name="phone" placeholder="(XXX) XXX-XX-XX" required />
                  </div>
                </label>
              </div>

              <span className="field__label">Выберите услугу</span>
              <div className="services services--selectable">
                {services.map((service) => (
                  <button
                    key={service.title}
                    type="button"
                    className={`service ${selectedService.title === service.title ? "service--selected" : ""}`}
                    onClick={() => setSelectedService(service)}
                  >
                    <h4>{service.title}</h4>
                    <p>
                      Продолжительность: {service.duration}
                      <br />
                      Стоимость: {service.price}
                    </p>
                  </button>
                ))}
              </div>

              <div className="consent">
                <label className="consent__checkbox">
                  <input type="checkbox" required />
                  <span className="consent__mark" aria-hidden="true"></span>
                  <span className="consent__text">
                    Я согласен с{" "}
                    <button type="button" className="rules-trigger" onClick={() => alert("Откройте правила")}>
                      Правилами записи и отмены консультаций
                    </button>
                  </span>
                </label>
              </div>

              <button type="submit" className="btn btn--primary form__submit">
                Отправить заявку
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="services-showcase">
        <div className="services-showcase__inner">
          <button
            className="slider__nav slider__nav--prev"
            aria-label="Предыдущая услуга"
            onClick={() => setSlide((s) => (s + services.length - 1) % services.length)}
          >
            ‹
          </button>
          <div className="services-slider" onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
            {services.map((service, idx) => (
              <article key={service.title} className={`service-slide ${idx === slide ? "is-active" : ""}`}>
                <div
                  className="slide__media"
                  style={{ backgroundImage: service.bg || "linear-gradient(135deg, #0f1014, #1c1e26)" }}
                ></div>
                <div className="slide__content">
                  <p className="eyebrow">Услуга</p>
                  <h3>{service.title}</h3>
                  <p className="slide__meta">
                    Продолжительность: {service.duration} · Стоимость: {service.price}
                  </p>
                  <p className="slide__text">{service.description}</p>
                </div>
              </article>
            ))}
          </div>
          <button
            className="slider__nav slider__nav--next"
            aria-label="Следующая услуга"
            onClick={() => setSlide((s) => (s + 1) % services.length)}
          >
            ›
          </button>
        </div>
      </section>

      <section className="articles" id="publications">
        <div className="articles__inner">
          <div className="articles__header">
            <p className="eyebrow">Публикации</p>
            <h2>Статьи для пациентов</h2>
            <p className="articles__lede">Подборка материалов по неврологии и психотерапии. Переписанные тексты доступны на сайте.</p>
          </div>
          <div className="articles__grid">
            {featuredArticles.map((art) => (
              <article key={art.slug} className="article-card">
                <div className="article-card__media"></div>
                <div className="article-card__body">
                  <span className="article-card__date">{art.date}</span>
                  <h3>{art.title}</h3>
                  <p>{art.excerpt}</p>
                  <Link className="article-card__link" to={`/articles/${art.slug}`}>
                    Читать полностью
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="articles__actions">
            <Link className="btn btn--ghost" to="/publications">
              Смотреть все
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};
